<template>
  <div>
    <div>
      <div class="accordion -simple row">
        <div v-for="(item, index) in accordionItems" :key="`accordion-item-${index}`" class="col-12">
          <div class="accordion__item pt-15" :class="{ 'is-active': activeItems.includes(index) }">
            <div class="accordion__button pb-15 d-flex items-center" @click="toggleAccordion(index)">
              <div
                class="button uppercase text-dark-1"
                :class="
                  company?.company_key === 'kineticestateagents'
                    ? 'text-24 md:text-22 !tw-font-medium'
                    : 'text-18 md:text-16 fw-500'
                "
                style="color: var(--c-text_primary)"
              >
                <span v-if="showNumbers">{{ index + 1 }}- </span>{{ item.title }}
              </div>

              <div
                class="accordion__icon size-40 flex-center bg-light-2 rounded-full mr-20"
                :class="{ 'is-active': activeItems.includes(index) }"
              >
                <i class="icon-plus" style="color: var(--c-text_primary)"></i>
                <i class="icon-minus-1" style="color: var(--c-text_primary)"></i>
              </div>
            </div>

            <div class="accordion__content" :ref="`accordion-content-${index}`">
              <div class="pt-30 mb-15" :ref="`accordion-inner-content-${index}`">
                <div v-if="item.html" class="text-18 md:text-16" v-html="item.content"></div>
                <p v-else class="text-18 md:text-16">
                  {{ item.content }}
                </p>

                <!-- Slot for custom content -->
                <slot
                  :name="item.slot || `item-${index}`"
                  :item="item"
                  :index="index"
                  :is-active="activeItems.includes(index)"
                ></slot>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { mapState } from 'pinia';
import { useWebsiteStore } from '~/units/website/store';

type AccordionItem = {
  title: string;
  content: string;
  html?: boolean;
  slot?: string;
};

export default defineNuxtComponent({
  name: 'Theme7Accordion',

  props: {
    items: {
      type: Array as PropType<AccordionItem[]>,
      default: () => [
        {
          title: 'DO YOU HAVE A STORE IN EUROPE?',
          content:
            'Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco.',
          html: false,
        },
        {
          title: 'WHAT ACCOMODATIONS ARE MADE FOR VISITORS WITH DISABILITIES?',
          content:
            'Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco.',
          html: false,
        },
        {
          title: 'CAN I PAY WITH VISA INSPIRE CARD?',
          content:
            'Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco.',
          html: false,
        },
        {
          title: 'WHAT SECURITY PRECAUTIONS DO YOU TAKE?',
          content:
            'Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco.',
          html: false,
        },
        {
          title: 'WHAT WOMEN WANT IN THE RESTROOM',
          content:
            'Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco.',
          html: false,
        },
      ],
    },

    allowMultiple: {
      type: Boolean,
      default: false,
    },

    showNumbers: {
      type: Boolean,
      default: true,
    },

    initialOpenItems: {
      type: Array as PropType<number[]>,
      default: () => [],
    },
  },

  data() {
    return {
      activeItems: [] as number[],
      resizeObserver: null as ResizeObserver | null,
    };
  },

  computed: {
    ...mapState(useWebsiteStore, ['company']),

    accordionItems(): AccordionItem[] {
      return this.items;
    },
  },

  watch: {
    items: {
      handler() {
        this.$nextTick(() => {
          // Re-setup observer for new items
          if (this.resizeObserver) {
            this.resizeObserver.disconnect();
          }
          this.setupResizeObserver();
          this.updateContentHeights();
        });
      },
      deep: true,
    },
  },

  mounted() {
    this.activeItems = [...this.initialOpenItems];
    this.initializeAccordion();

    this.$nextTick(() => {
      setTimeout(() => {
        this.setInitialHeights();
      }, 100);

      setTimeout(() => {
        this.updateContentHeights();
      }, 300);

      setTimeout(() => {
        this.updateContentHeights();
      }, 600);

      setTimeout(() => {
        this.updateContentHeights();
      }, 1000);
    });

    this.setupResizeObserver();
  },

  beforeUnmount() {
    // ResizeObserver'ı temizle
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  },

  methods: {
    toggleAccordion(index: number) {
      if (this.allowMultiple) {
        // Multiple items can be open
        const activeIndex = this.activeItems.indexOf(index);
        if (activeIndex > -1) {
          this.activeItems.splice(activeIndex, 1);
        } else {
          this.activeItems.push(index);
        }
      } else {
        // Only one item can be open
        if (this.activeItems.includes(index)) {
          this.activeItems = [];
        } else {
          this.activeItems = [index];
        }
      }

      // Update heights after state change
      this.$nextTick(() => {
        this.updateContentHeights();
      });

      this.$emit('accordion-toggle', {
        index,
        item: this.accordionItems[index],
        isOpen: this.activeItems.includes(index),
        activeItems: [...this.activeItems],
      });
    },

    initializeAccordion() {
      // Add any initialization logic here if needed
    },

    getContentHeight(index: number): number {
      const contentRef = this.$refs[`accordion-content-${index}`] as HTMLElement | HTMLElement[];
      const element = Array.isArray(contentRef) ? contentRef[0] : contentRef;
      if (element && element.scrollHeight !== undefined) {
        return element.scrollHeight;
      }
      return 0;
    },

    setInitialHeights() {
      this.activeItems.forEach((index) => {
        const contentRef = this.$refs[`accordion-content-${index}`] as HTMLElement | HTMLElement[];
        const element = Array.isArray(contentRef) ? contentRef[0] : contentRef;

        if (element && element.style) {
          const height = element.scrollHeight;
          element.style.maxHeight = height + 'px';
        } else {
          console.warn(`Element for index ${index} is not available or has no style property`);
        }
      });
    },

    updateContentHeights() {
      // Update heights for all accordion items
      this.accordionItems.forEach((_, index) => {
        const contentRef = this.$refs[`accordion-content-${index}`] as HTMLElement | HTMLElement[];
        const element = Array.isArray(contentRef) ? contentRef[0] : contentRef;
        if (element && element.style) {
          if (this.activeItems.includes(index)) {
            element.style.maxHeight = element.scrollHeight + 'px';
          } else {
            element.style.maxHeight = '0px';
          }
        }
      });
    },

    openItem(index: number) {
      if (!this.activeItems.includes(index)) {
        if (this.allowMultiple) {
          this.activeItems.push(index);
        } else {
          this.activeItems = [index];
        }
        this.$nextTick(() => {
          this.updateContentHeights();
        });
      }
    },

    closeItem(index: number) {
      const activeIndex = this.activeItems.indexOf(index);
      if (activeIndex > -1) {
        this.activeItems.splice(activeIndex, 1);
        this.$nextTick(() => {
          this.updateContentHeights();
        });
      }
    },

    closeAll() {
      this.activeItems = [];
      this.$nextTick(() => {
        this.updateContentHeights();
      });
    },

    openAll() {
      if (this.allowMultiple) {
        this.activeItems = this.accordionItems.map((_, index) => index);
        this.$nextTick(() => {
          this.updateContentHeights();
        });
      }
    },

    setupResizeObserver() {
      // ResizeObserver destekleniyorsa
      if (typeof ResizeObserver !== 'undefined') {
        this.resizeObserver = new ResizeObserver(() => {
          // Aktif accordion'ların height'ını güncelle
          if (this.activeItems.length > 0) {
            this.$nextTick(() => {
              this.updateContentHeights();
            });
          }
        });

        // Tüm accordion content'lerini gözlemle
        this.accordionItems.forEach((_, index) => {
          // Observe inner content instead of outer wrapper
          const contentRef = this.$refs[`accordion-inner-content-${index}`] as HTMLElement | HTMLElement[];
          const element = Array.isArray(contentRef) ? contentRef[0] : contentRef;
          if (element) {
            this.resizeObserver?.observe(element);
          }
        });
      }
    },
  },
});
</script>
